// app/src/api/departmentApi.ts

import { createAsyncThunk } from "@reduxjs/toolkit"
import { DepartmentSerializer } from "@/types/department"
import { CommonResponse, CommonTResponse, CommonReferenceResponse } from "@/types/common"
import { serverApi, getAuthHeaders, handleApiResponse, handleReferenceResponse } from "@core/api/core"

// ──────────────────────────────────────────────────────────────────────────────
// 진료과목 목록 조회
export const getDepartments = async (): Promise<DepartmentSerializer[]> => {
  const requestUrl = `/department/`
  const response = await serverApi.get<CommonTResponse<DepartmentSerializer[]>>(requestUrl)
  // console.log("response", response.data.result)

  // handleApiResponse가 이미 result/results 필드를 추출함
  const data = handleApiResponse<DepartmentSerializer[]>(response)

  // 🛡️ 배열 검사 - handleApiResponse가 배열을 반환하는지 확인
  if (Array.isArray(data)) {
    return data
  }

  // 단일 객체가 온 경우 배열로 감싸기
  return [data]
}

// ──────────────────────────────────────────────────────────────────────────────
// 생성
export const createDepartment = async (payload: DepartmentSerializer): Promise<boolean> => {
  const requestUrl = `/department/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 수정
export const updateDepartment = async (payload: DepartmentSerializer): Promise<boolean> => {
  const requestUrl = `/department/${payload.departmentUuid}/`
  const response = await serverApi.patch<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 삭제
export const deleteDepartment = async (uuid: string): Promise<boolean> => {
  const requestUrl = `/department/${uuid}/`
  const response = await serverApi.delete<CommonResponse>(requestUrl)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────

export const fetchDepartments = createAsyncThunk<
  DepartmentSerializer[], // 성공 시 반환할 타입
  void, // 인자로 받을 객체 타입
  { rejectValue: string }
>("department/fetchDepartments", async (_, { rejectWithValue }) => {
  try {
    const requestUrl = `/reference/department/`
    const response = await serverApi.get<CommonReferenceResponse>(requestUrl)
    const data = handleReferenceResponse(response)

    const datas: DepartmentSerializer[] = data.results.map(row => {
      // columns[i] ↔ row[i] 매핑
      const obj = Object.fromEntries(data.columns.map((col, i) => [col, row[i]])) as Record<string, number | string>
      const rawIsActive = obj["isActive"]

      return {
        departmentUuid: String(obj["departmentUuid"]),
        departmentName: String(obj["departmentName"]),
        itemOrder: Number(obj["itemOrder"] ?? 0),
        isActive: typeof rawIsActive === "boolean" ? rawIsActive : String(rawIsActive).toLowerCase() !== "false" && String(rawIsActive) !== "0",
      }
    })
    // console.log("fetchDepartments", datas)
    return datas
  } catch (error: any) {
    return rejectWithValue(error.message || "진료과목 조회 중 오류가 발생했습니다.")
  }
})
